import { tt } from '@agos/shared'

/**
 * The onboarding wizard's rules — SAAS.md §3.16.
 *
 * **What this screen does, stated against what legacy's did.** Legacy's `POST /super-admin/franchises`
 * with `autoProvision` or `deployStack` set creates a database, an NPM proxy host, a Portainer stack
 * and a WireGuard peer. **AGOS's does none of that**: those two members appear nowhere in the backend,
 * and `create` refuses outright without a `databaseUrl` — *"stack sa nasadzuje mimo API (pozri
 * INSTRUCTIONS.md)"*. SAAS.md §5 is why, and it is explicit: *"a 'just testing' franchise on prod NPM
 * is an incident."*
 *
 * So the wizard asks for the database URL of a stack that already exists, and the two switches are
 * **gone** rather than kept as decoration. They were a faithful port of legacy's form driving a server
 * that answers differently, which made every press of *Vytvoriť* a 400 — and a control that promises
 * automation the server refuses is the owner's own O25 complaint (§9.285) indoors.
 *
 * **What the server still does on its own** is seed the first `main_admin` **inside the tenant
 * database** and answer with `provisioningNotes` saying whether that landed. Those notes are the only
 * record of it, which is why {@link notesOf} reads the key the server actually sends.
 *
 * **Measured, and still true:** with the provisioning credentials empty, as they are locally, both
 * `POST /franchises/{id}/wireguard/provision` and `POST /marketing-pages/home/domain` answer **503**
 * naming the variables they want and write nothing — `requireConfigured()` is the first statement of
 * `provisionPeer`. Those endpoints remain deliberately unwired (§5); this screen never called them.
 */

/** Legacy's own pattern, unchanged: the slug becomes a database name and a subdomain. */
export const SLUG_PATTERN = /^[a-z0-9_-]+$/

export interface NewFranchiseDraft {
  name: string
  slug: string
  displayName: string
  domain: string
  /**
   * The tenant's own database - §3.16.
   *
   * **Required, because the server requires it**: `FranchiseAdminService.create` refuses without one
   * and says why - *"stack sa nasadzuje mimo API"*. The stack is deployed by `INSTRUCTIONS.md`
   * before a franchise is recorded, so by the time this form is open the URL exists. The server
   * masks the password when it answers, so it is never read back out.
   */
  databaseUrl: string
  adminEmail: string
  adminPassword: string
  adminName: string
  /**
   * The franchise's brand - §3.115.
   *
   * **`CentralRegistryService` serves both to the customer app**, where `branding.ts` writes them as CSS
   * variables. Legacy's wizard collects them on its first step with defaults filled in; AGOS's did not
   * collect them at all, so every franchise it created served the customer app a null brand until
   * somebody remembered to set it on the detail screen.
   */
  primaryColor: string
  secondaryColor: string
}

/**
 * The brand a new franchise starts with - `SAAS.md §3.115`, legacy's `#f97316` and `#0ea5e9`.
 *
 * **Ported rather than chosen.** These are the two values legacy's wizard seeds its colour boxes with,
 * so a franchise created through either system looks the same on its first day. Changing them is a
 * product decision about what an unbranded pizzeria looks like, not a port.
 */
export const DEFAULT_PRIMARY_COLOUR = '#f97316'
export const DEFAULT_SECONDARY_COLOUR = '#0ea5e9'

/** `#f97316`, `#FFF`, or empty. Anything else is a typo, and a typo reaches the customer app. */
export const COLOUR_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i

export const EMPTY_DRAFT: NewFranchiseDraft = {
  name: '',
  slug: '',
  displayName: '',
  domain: '',
  databaseUrl: '',
  adminEmail: '',
  adminPassword: '',
  adminName: '',
  // §3.115 - legacy's own two defaults, pre-filled for the same reason it pre-fills them: a franchise
  // created with no brand serves the customer app no colour at all, and *the operator will set it
  // afterwards* is a hope rather than a default.
  primaryColor: DEFAULT_PRIMARY_COLOUR,
  secondaryColor: DEFAULT_SECONDARY_COLOUR,
}

/**
 * What a tenant database URL has to look like.
 *
 * Deliberately loose about the host and the credentials - this is a paste from a runbook, not a
 * parser - and strict about the scheme, because the one mistake worth catching here is a name or a
 * host typed where a URL belongs.
 */
export const DATABASE_URL_PATTERN = /^(jdbc:)?postgres(ql)?:\/\/\S+$/i

/**
 * `Pizza Majstro – Šurany` → `pizza-majstro-surany`.
 *
 * Diacritics are stripped rather than dropped: `Šurany` must become `surany`, not `urany`, because the
 * slug ends up in a database name and a hostname.
 */
export function slugify(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * The wizard's steps - `SAAS.md §3.102`.
 *
 * **`'integrations'` is gone, and it was not a missing feature - it was a blank screen.** It was in
 * this type, in `STEPS`, and in the *Pokračovať* button's own order, with **no render block in
 * `NewFranchisePage`** - so from *Admin* the button landed on a heading, a button and empty space
 * that had to be clicked again. `stepProblem` had no branch for it either, so it fell through to
 * `''` and was instantly leavable.
 *
 * It is removed rather than built because `FranchiseAdminService.create` accepts **none** of
 * legacy's step-2 credentials - they live behind `PATCH …/{id}/integrations`, which §3.85 and §3.86
 * turned into a real form - and writing them at creation means a second request into a tenant
 * database that may not be migrated yet. The wizard's own result step exists to report *"Admin seed
 * FAILED (schema may not be migrated yet)"*, so that is not hypothetical. And this file already
 * argues the case, about the switches §3.16 removed: *"A control that claims automation the server
 * refuses is the owner's own O25 complaint, indoors."*
 *
 * `STEPS` was read by **nothing**; it is now what the progress indicator is built from, so a step
 * added here and not rendered shows up as a dot nobody can reach rather than as a blank page. The
 * order is the order they are shown and walked in.
 */
export type Step = 'basic' | 'admin' | 'provisioning' | 'result'

export const STEPS: readonly Step[] = ['basic', 'admin', 'provisioning', 'result']

/** What the dot says, which is the whole value of the indicator legacy has and this did not. */
export function stepLabel(step: Step): string {
  if (step === 'basic') return tt('Základ')
  if (step === 'admin') return tt('Administrátor')
  if (step === 'provisioning') return tt('Nasadenie')
  return tt('Hotovo')
}

/** Which step comes next, or `null` at the end - the walk the *Pokračovať* button does. */
export function stepAfter(step: Step): Step | null {
  // **Derived from `STEPS` rather than a second list.** The button used to carry its own array, and
  // that array was where `'integrations'` survived: two orderings of one sequence, which is the
  // shape of most of this repository's defects (`agos-one-reader-spelled-twice`).
  const next = STEPS[STEPS.indexOf(step) + 1]
  return next ?? null
}

/** Why this step cannot be left, or `''` when it can. */
/**
 * Why this step cannot be left - `SAAS.md` §3.107 for the `null`.
 *
 * **`existingSlugs` is `null` when the list could not be read**, and that is a third state rather
 * than an absence. An empty array means *no franchise has this slug*; `null` means *I could not find
 * out*, and the two must not share a value - `agos-a-no-backfill-column-is-ambiguous` one layer up.
 *
 * The distinction is load-bearing here and not tidiness. This function's own next comment says why:
 * *"A duplicate slug is a duplicate database name. Better refused here than half-provisioned."*
 * Before §3.107 a failed read handed this an empty array, `includes` answered false, and the wizard
 * let the operator through to provision a franchise whose slug collides with a live database name -
 * the guard did not report a problem, it stopped being a guard. **An unverifiable check refuses.**
 */
export function stepProblem(
  step: Step,
  draft: NewFranchiseDraft,
  existingSlugs: readonly string[] | null,
): string {
  if (step === 'basic') {
    if (draft.name.trim() === '') return tt('Názov je povinný.')
    if (!SLUG_PATTERN.test(draft.slug)) {
      return tt('Skratka smie obsahovať len malé písmená, číslice, pomlčku a podčiarkovník.')
    }
    // A duplicate slug is a duplicate database name. Better refused here than half-provisioned.
    //
    // §3.107 - and *unknown* is refused for the same reason. The refusal names the cause, because
    // "Franšíza s touto skratkou už existuje" would be a claim this screen cannot make.
    if (existingSlugs === null) {
      return tt('Zoznam existujúcich franšíz sa nepodarilo načítať - skratku nie je ako overiť.')
    }
    if (existingSlugs.includes(draft.slug)) return tt('Franšíza s touto skratkou už existuje.')
    /* §3.115 - **refused here rather than sent.** These two reach the customer app as CSS variables, so
     * `#f9731` renders as no colour at all and the site looks broken with nothing on any screen saying
     * why. Empty is allowed: a franchise may deliberately have no brand of its own. */
    for (const colour of [draft.primaryColor, draft.secondaryColor]) {
      if (colour.trim() !== '' && !COLOUR_PATTERN.test(colour.trim())) {
        return tt('Farba musí byť v tvare #f97316.')
      }
    }
    return ''
  }
  if (step === 'admin') {
    if (draft.adminEmail.trim() === '') return tt('E-mail administrátora je povinný.')
    if (draft.adminPassword.length < 6) return tt('Heslo musí mať aspoň 6 znakov.')
    return ''
  }
  if (step === 'provisioning') {
    // §3.16 - the server refuses without this, so refusing here says so before the slug has been
    // typed out to confirm.
    if (draft.databaseUrl.trim() === '') return tt('Adresa databázy je povinná.')
    if (!DATABASE_URL_PATTERN.test(draft.databaseUrl.trim())) {
      return tt('Adresa databázy musí začínať postgresql:// alebo jdbc:postgresql://')
    }
    return ''
  }
  return ''
}

export function mayLeave(
  step: Step,
  draft: NewFranchiseDraft,
  existingSlugs: readonly string[] | null,
): boolean {
  return stepProblem(step, draft, existingSlugs) === ''
}

/**
 * What the last step is about to do, in words, before it is done.
 *
 * A wizard that says *Create* and then builds a proxy host is not asking. Each switch names its own
 * consequence, so the confirmation is about the thing rather than about the button.
 */
export function provisioningSummary(draft: NewFranchiseDraft): string[] {
  // §3.16 - what the server actually does, and nothing it does not. `autoProvision` and
  // `deployStack` were sent here and appear **nowhere** in the backend: two switches promising
  // infrastructure this platform deliberately will not create from an API call (§5). A control that
  // claims automation the server refuses is the owner's own O25 complaint, indoors.
  const steps = [tt('Zapíše sa záznam franšízy a jej adresa databázy.')]
  if (draft.domain.trim() !== '') steps.push(`${tt('Doména')}: ${draft.domain.trim()}`)
  if (draft.adminEmail.trim() !== '') {
    steps.push(`${tt('Vytvorí sa prvý administrátor')}: ${draft.adminEmail.trim()}`)
  }
  steps.push(tt('Infraštruktúra sa nevytvára - stack sa nasadzuje podľa INSTRUCTIONS.md.'))
  return steps
}

/** Typed out, like erasing a franchise — for the same reason, in the other direction. */
export function creationConfirmed(typed: string, slug: string): boolean {
  return typed.trim() === slug && slug !== ''
}

/** The body, with empty optionals omitted rather than sent blank. */
export function createPayload(draft: NewFranchiseDraft): Record<string, unknown> {
  const orUndefined = (value: string) => (value.trim() === '' ? undefined : value.trim())
  return {
    name: draft.name.trim(),
    slug: draft.slug,
    displayName: orUndefined(draft.displayName),
    domains: draft.domain.trim() === '' ? undefined : [draft.domain.trim()],
    // §3.16 - the field the server refuses to create a franchise without.
    // §3.16 - the field the server refuses to create a franchise without.
    databaseUrl: draft.databaseUrl.trim(),
    adminEmail: orUndefined(draft.adminEmail),
    adminPassword: draft.adminPassword === '' ? undefined : draft.adminPassword,
    adminName: orUndefined(draft.adminName),
    // §3.115 - `POST /super-admin/franchises` has named both in its INSERT since it was written and
    // this payload sent neither, so every franchise AGOS created started with a null brand.
    primaryColor: orUndefined(draft.primaryColor),
    secondaryColor: orUndefined(draft.secondaryColor),
  }
}

/**
 * The notes the server sends back about what it actually did.
 *
 * Shown verbatim: a provisioning run that skipped a step says so, and paraphrasing it would be
 * paraphrasing the only record of what exists.
 */
/**
 * The id of the franchise that was just created, or null - §3.136.
 *
 * **Both response shapes**, for the reason §3.16 recorded one line below: `notesOf` had only legacy's key, so the
 * note saying *"Admin seed FAILED"* was never shown. A reader that takes one shape when two exist is how that
 * happened, so this takes `franchise.id` and a bare `id` alike.
 *
 * **Null rather than a guess.** The caller draws a link to this franchise's integrations, and a dead link on a
 * success screen is worse than no link.
 */
export function createdFranchiseIdOf(payload: unknown): number | null {
  if (typeof payload !== 'object' || payload === null) return null
  const said = payload as { franchise?: unknown; id?: unknown }
  const row =
    typeof said.franchise === 'object' && said.franchise !== null
      ? (said.franchise as { id?: unknown })
      : said
  const id = row.id
  if (typeof id === 'number' && Number.isInteger(id) && id > 0) return id
  if (typeof id === 'string' && /^[0-9]+$/.test(id) && Number(id) > 0) return Number(id)
  return null
}

export function notesOf(payload: unknown): string[] {
  if (typeof payload !== 'object' || payload === null) return []
  // §3.16 - **`provisioningNotes` is what AGOS answers**; `notes` is legacy's key and this read only
  // ever had that one, so the line saying whether the first admin actually landed - *"Admin seed
  // FAILED (schema may not be migrated yet)"* - was never shown. Both are accepted: a
  // legacy-shaped response still renders.
  const said = payload as { provisioningNotes?: unknown; notes?: unknown }
  const notes = Array.isArray(said.provisioningNotes) ? said.provisioningNotes : said.notes
  if (!Array.isArray(notes)) return []
  return notes.filter((note): note is string => typeof note === 'string' && note.trim() !== '')
}
